// components/CalendarBase.tsx
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import type { OnArgs } from 'react-calendar';

import { Icon } from '@/components/Icon';
import type { Weekday } from '@/lib/boutique/helpers';
import { isDateDisabled } from '@/lib/boutique/helpers';
import * as S from '@/views/AppointmentView/AppointmentView.styles';

type CalendarBaseProps = {
  futureDate: Date;
  handleMonthChange:
    | (({ action, activeStartDate, value, view }: OnArgs) => void)
    | undefined;
  nonWorkingDays?: string[];
  onChange: (newDate: any) => void;
  tomorrow: Date;
  unavailableAppointmentWeekdays?: Weekday[];
  value: string;
};

export const CalendarBase = ({
  futureDate,
  handleMonthChange,
  nonWorkingDays = [],
  onChange,
  tomorrow,
  unavailableAppointmentWeekdays = [],
  value,
}: CalendarBaseProps) => {
  const calendarWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const calendarEl = calendarWrapperRef.current;
    if (!calendarEl) return;

    const tiles = calendarEl.querySelectorAll<HTMLButtonElement>(
      '.react-calendar__tile',
    );

    tiles.forEach((tile) => {
      if (tile.disabled) {
        tile.setAttribute('tabindex', '-1');
        return;
      }

      tile.setAttribute('tabindex', '0');
      tile.setAttribute(
        'aria-pressed',
        tile.classList.contains('react-calendar__tile--active')
          ? 'true'
          : 'false',
      );
    });
  }, [value, nonWorkingDays, unavailableAppointmentWeekdays]);

  const parsedValue = value
    ? new Date(
        parseInt(value.split('-')[0]),
        parseInt(value.split('-')[1]) - 1,
        parseInt(value.split('-')[2]),
      )
    : undefined;

  return (
    <S.CalendarWrapper ref={calendarWrapperRef} bg='white'>
      <S.CalendarStyled
        formatMonthYear={(_, date) => dayjs(date).format('MMMM')}
        formatShortWeekday={(_locale, value) =>
          ['S', 'M', 'T', 'W', 'T', 'F', 'S'][value.getDay()]
        }
        locale='EN'
        maxDate={futureDate}
        minDate={tomorrow}
        nextLabel={
          <Icon
            aria-label='Next month'
            color='primary'
            name='chevronRight'
            size='l'
            variant='regular'
          />
        }
        onActiveStartDateChange={handleMonthChange}
        onChange={onChange}
        prevLabel={
          <Icon
            aria-label='Previous month'
            color='primary'
            name='chevronLeft'
            size='l'
            variant='regular'
          />
        }
        showNeighboringMonth={false}
        tileDisabled={({ date }) =>
          isDateDisabled(date, unavailableAppointmentWeekdays, nonWorkingDays)
        }
        value={parsedValue}
      />
    </S.CalendarWrapper>
  );
};
